// 小窗: a small always-on-top window (the Document Picture-in-Picture API, Chrome / Edge 116+) for when Deck isn't in
// front. The map is the window: a small map of the area round me (附近的人: my otter in the middle, the rooms and walls,
// whoever is near, a green ring while they talk; click someone for their card: 打招呼, 走过去, 邀请到身边 ...) or, in a
// 会议室, who is in it and the active speaker's camera. Around it two slim bars of icon buttons, as in a video call:
// above, my status (a dot; its list: 在线 / 离开 / 忙碌 / 请勿打扰), what the map shows and its count, and 回到 Deck
// (brings the page to the front and closes the window); below, the mic (red while muted), the speaker (red when off; a
// click opens a small bar with 关闭声音 and the volume), and in a meeting the camera and 离开 / 挂断, 停止共享 while I
// share. Above the map: invites to me (接受 / 稍后) and a 双人互动 request to me (「X 想和你蹭蹭」 好呀 / 下次吧 and its
// time running out: public/duo.js). It's the same page (same JS realm): the window is a small
// view drawn from the same state every POLL_MS, and every button calls what the page's own buttons call, so both stay
// in step. Closing it changes nothing else (the call goes on).
// Opened by 「小窗」 at the top left (a click: the browser wants a user gesture), or by itself when the tab goes to the
// background if 设置 → 小窗 → 切走时自动开小窗 is on and the browser allows it (Chrome does for a page in a call, through
// the media session's "enterpictureinpicture" action; elsewhere nothing happens, and nothing nags). Opened by itself, it
// closes when the tab comes back.
// No Document PiP (Safari, Firefox, phones): no 「小窗」 button; invites can come as system notifications instead
// (设置 → 小窗 → 切走时用系统通知提醒邀请), as they also do in Chrome when the tab is in the background without the window.
import { icon, put, label } from '/icons.js';
import { dropdown } from '/dropdown.js';
import { t, onLang, lang, uiFont } from '/i18n.js';

const AUTO_KEY = 'deck.pipAuto', NOTIFY_KEY = 'deck.inviteNotify';
const W = 320, H = 420, POLL_MS = 250;
// the minimap: CSS px per tile, the hearing ring (the office's HEAR_RADIUS, src/office/voice.ts), avatar size
const MM_TILE = 12, MM_HEAR = 5, MM_AV = 24, MM_FRAME_MS = 33; // (redrawn at most ~30 times a second)
const MM_SEA = [59, 143, 196], MM_DECK = [233, 210, 164], MM_WALL = [107, 74, 43]; // sea, deck, walls and furniture
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const pref = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const setPref = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

// The icon buttons' tooltips (also what a screen reader says): pip.tip.<k> in the catalogs (public/locales/*.json).
const tip = (k, vars) => t(`pip.tip.${k}`, vars);

const CSS = `:root{--ui-font:-apple-system,system-ui,sans-serif}
:root:not(:lang(zh)){--ui-font:-apple-system,system-ui,"PingFang SC","Hiragino Sans GB","Microsoft YaHei","Noto Sans CJK SC","Noto Sans SC","Source Han Sans SC",sans-serif}
*{box-sizing:border-box}
html,body{margin:0;height:100%;background:#2f84b8;font:13px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;overflow:hidden}
body{display:flex;flex-direction:column;gap:6px;padding:6px}
button{font:inherit;color:#1b1410;cursor:pointer}
.card{background:#fff8e7;border:3px solid #1b1410;border-radius:12px;padding:6px 8px}
.bar{flex:none;display:flex;align-items:center;gap:4px;min-width:0;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;padding:3px}
.bar.bot{justify-content:center;position:relative}
.bar .t{flex:1;min-width:0;display:flex;align-items:baseline;gap:6px;padding:0 4px;font-weight:700;white-space:nowrap;overflow:hidden}
.bar .t .n{flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis} .bar .t .c{flex:0 100 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;font-weight:400;color:#776c5c;font-size:12px}
.ib{flex:none;width:30px;height:30px;padding:0;display:inline-flex;align-items:center;justify-content:center;gap:0;border:2px solid #1b1410;border-radius:8px;background:#fff;line-height:0}
.ib.on,.ib.go,.ib[aria-expanded=true]{background:#FDD03B} .ib.off,.ib.live,.ib.red{background:#ef4444}
.ib:disabled{opacity:.45;cursor:default}
.ib:focus{outline:none} .ib:focus-visible{outline:2px dashed #1b1410;outline-offset:2px}
.pop{position:absolute;left:3px;right:3px;bottom:calc(100% + 6px);display:flex;align-items:center;gap:6px;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;box-shadow:0 2px 0 #1b1410;padding:4px 6px;z-index:5}
.pop[hidden]{display:none}
.pop input{flex:1;min-width:0;accent-color:#1b1410}
.pop .n{width:34px;text-align:right;font-size:12px;font-variant-numeric:tabular-nums}
.main{flex:1;min-height:0;overflow:auto;display:flex;flex-direction:column;gap:6px}
.room{flex:1;min-height:0;display:flex;flex-direction:column}
.room.card{overflow:auto}
.vid{position:relative;aspect-ratio:16/9;background:#111;border:2px solid #1b1410;border-radius:8px;overflow:hidden;margin-bottom:4px;flex:none}
.vid video{width:100%;height:100%;object-fit:cover;display:block} .vid.mine video{transform:scaleX(-1)}
.vid .who{position:absolute;left:4px;bottom:4px;background:rgba(20,20,24,.72);color:#fff;border-radius:6px;padding:0 6px;font-size:11px}
.ppl{display:flex;flex-wrap:wrap;gap:6px}
.av{display:flex;flex-direction:column;align-items:center;width:50px;font-size:11px}
.av canvas{width:36px;height:36px;image-rendering:pixelated;background:#e9d8b4;border:2px solid #1b1410;border-radius:50%}
.av.talk canvas{border-color:#22c55e;box-shadow:0 0 0 3px #22c55e}
.av span{max-width:50px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mm{position:relative;flex:1;min-height:110px}
.mm canvas{display:block;width:100%;height:100%;border:2px solid #1b1410;border-radius:8px;background:rgb(${MM_SEA});image-rendering:pixelated}
.mm .empty{position:absolute;left:6px;right:6px;top:6px;background:rgba(255,248,231,.92);border:1px solid #1b1410;border-radius:6px;padding:1px 6px;color:#5b4f40;font-size:12px;pointer-events:none}
.pc{position:absolute;left:4px;right:4px;bottom:4px;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;box-shadow:0 2px 0 #1b1410;padding:5px 6px;display:flex;flex-direction:column;gap:4px}
.pc[hidden]{display:none}
.pc .top{display:flex;align-items:center;gap:6px}
.pc .top canvas{width:32px;height:32px;image-rendering:pixelated;background:#e9d8b4;border:2px solid #1b1410;border-radius:50%;flex:none}
.pc .who{flex:1;min-width:0;display:flex;flex-direction:column}
.pc .nm{font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pc .wh{font-size:11px;color:#776c5c;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pc .x{border:0;background:none;padding:0 2px;line-height:0;align-self:flex-start}
.pc .acts{display:flex;flex-wrap:wrap;gap:4px}
.pc .acts>*{display:inline-flex;align-items:center;gap:4px;border:2px solid #1b1410;border-radius:999px;padding:1px 8px;background:#fff;font-size:12px;white-space:nowrap;color:#1b1410;text-decoration:none;cursor:pointer}
.pc .acts>button.go{background:#FDD03B} .pc .acts>button:disabled{opacity:.5;cursor:default}
.inv{background:#fff8e7;border:3px solid #1b1410;border-radius:12px;padding:6px 8px;flex:none}
.inv.quiet{border-color:#ef4444}
.inv .t{display:flex;gap:6px;font-weight:600;word-break:break-word}
.inv .bt{display:flex;gap:6px;margin-top:5px}
.inv button{border:2px solid #1b1410;border-radius:999px;padding:2px 10px;background:#fff;font-size:12px;display:inline-flex;align-items:center;gap:4px}
.inv button.go{background:#FDD03B} .inv .dt{height:4px;background:#e9d8b4;border-radius:2px;overflow:hidden;margin-top:5px} .inv .dt i{display:block;height:100%;background:#1b1410} .inv button:disabled{opacity:.5;cursor:default}`;

/**
 * @param o {me, peers, hearing, caster, speaking(p), lookOf, buildSheet, otterImgs, agentName, statusOf, STATES,
 *   setStatus(s), mic: {muted, ready, toggle}, share: {live, stop}, speaker: {volume, off, setVolume, setOff},
 *   meeting: {info(), toggleCam(), leave()}, members: {list(), answer(id, a), onNew(fn)}, poke() (I'm here: status.js),
 *   tick(dt) (the map's step: walking, while the page is hidden and draws no frames), dndHint,
 *   toast, addSettingsSection, MAP (the map as it is now: tiles, rooms), zoneAt(x, y), statusLine(s),
 *   card: {actions(p): [{icon, label, disabled?, title?, go?}], profile(p): url | null} (a person's card on the minimap),
 *   duo: {pending(): {id, text, icon, agent, left} | null, answer(yes)} (public/duo.js: a 双人互动 request to me)}
 * @returns {{open(): boolean}} open: is the window up
 */
export function initPip(o) {
  const api = window.documentPictureInPicture;
  const supported = !!api && typeof api.requestWindow === 'function';
  let win = null, auto = false, ui = null, opening = null;

  // ---- the 「小窗」 button (top left, next to 设置); none where the API isn't
  const btn = label(el('button'), 'minimize', t('pip.button')); btn.id = 'pipBtn'; btn.type = 'button';
  btn.title = t('pip.openTip');
  btn.style.cssText = 'order:1;box-sizing:border-box;height:30px;background:#fff8e7;border:2px solid #1b1410;border-radius:10px;padding:0 10px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;cursor:pointer';
  btn.onclick = () => (win ? close() : openWin(false));
  let setBtn = null; // 设置 → 小窗's 打开小窗 / 关闭小窗, while it's shown
  /** The window came or went (by any way: a button, 回到 Deck, its own ×): the buttons that say so follow. */
  function sync() {
    btn.classList.toggle('on', !!win); btn.title = t(win ? 'pip.close' : 'pip.openTip');
    if (setBtn?.isConnected) setBtn.textContent = t(win ? 'pip.close' : 'pip.open'); else setBtn = null;
  }
  // a language change: the 「小窗」 button, and the window's words (the parts drawn once, and what waits for a change)
  onLang(() => {
    label(btn, 'minimize', t('pip.button')); sync();
    if (ui) { ui.relabel(); ui.dd.el.dataset.k = ''; for (const k in ui.sig) ui.sig[k] = ''; draw(); }
  });
  if (supported) { const s = document.createElement('style'); s.textContent = '#pipBtn.on{background:#FDD03B !important}'; document.head.append(s); }
  if (supported) { const tl = document.getElementById('topleft'); (tl || document.body).appendChild(btn); }

  if (supported) setTimeout(() => sheet(), 3000); // ready before the first 小窗
  /** Open it (once: a second ask while one is on its way waits for that one). */
  function openWin(byItself) {
    if (!supported || win || !o.me()) return Promise.resolve(!!win);
    return (opening ??= (async () => {
      let w;
      try { w = await api.requestWindow({ width: W, height: H }); }
      catch { return false; } // no user gesture (an automatic try), or refused: say nothing
      finally { opening = null; }
      if (win && win !== w) close();
      win = w; auto = byItself;
      build(w.document);
      // its own clock: the window is in view, the page behind it may not be (a hidden page's timers run once a second
      // and it draws no frames). The redraw and, with the page hidden, the walking (o.tick) run on this window's frames.
      let last = w.performance.now(), drawn = 0; // (its frames are timed on its own clock)
      const frame = (now) => {
        if (win !== w) return;
        const dt = Math.min(1, Math.max(0, (now - last) / 1000)); last = now; // slow frames (a busy machine) still walk at full speed, in 50 ms steps
        if (document.hidden && dt > 0) { let left = dt; while (left > 1e-3) { const d = Math.min(0.05, left); o.tick?.(d); left -= d; } }
        if (now - drawn >= POLL_MS) { drawn = now; draw(); }
        if (now - mapAt >= MM_FRAME_MS) { mapAt = now; drawMap(); }
        w.requestAnimationFrame(frame);
      };
      w.requestAnimationFrame(frame);
      w.addEventListener('pagehide', () => { if (win === w) { stop(); win = null; sync(); } });
      sync();
      draw();
      w.setTimeout(() => o.poke(), 2500); // (again once the office has echoed that 离开 back)
      o.poke(); // the tab went to the background just now and status.js said 离开 (automatic): with the 小窗 up I'm here
      return true;
    })());
  }
  function close() { const w = win; win = null; stop(); sync(); try { w?.close(); } catch {} }
  function stop() { if (ui) { ui.video.srcObject = null; ui = null; } avatars.clear(); sel = null; hover = null; mapAt = 0; } // (mapAt: the next window's clock starts again at 0)
  /** A button in the window did something: show it now, not at the next redraw. */
  const act = (f) => (...a) => { try { f(...a); } finally { draw(); } };

  // ---- auto: the tab going to the background
  const autoOn = () => pref(AUTO_KEY) === '1';
  function wireAuto() {
    if (!supported) return;
    // Chrome opens it by itself for a page in a call (mic or camera on) through the media session, where it can
    try { navigator.mediaSession?.setActionHandler('enterpictureinpicture', autoOn() ? () => { openWin(true); } : null); } catch {} // unknown action: an older browser
  }
  wireAuto();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { if (autoOn() && !win) openWin(true); } // without a gesture this is refused, quietly
    else if (win && auto) close(); // back: the window it opened by itself goes
  });

  // ---- invites as system notifications, when nobody would see the card
  const notifyOn = () => 'Notification' in window && Notification.permission === 'granted' && pref(NOTIFY_KEY) !== '0';
  o.members.onNew((c) => {
    if (!document.hidden || win || !notifyOn()) return;
    try {
      const n = new Notification(c.text, { body: t(c.later ? 'pip.notify.bodyLater' : 'pip.notify.body'), tag: 'deck-' + c.id, icon: '/icons/icon-192.png', silent: !!c.quiet });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {}
  });

  // ---- 设置 → 小窗
  o.addSettingsSection({ id: 'pip', title: () => t('pip.settings.title'), icon: 'minimize', order: 14, render(box) {
    const row = (text, on, set) => {
      const l = el('label'); l.style.cssText = 'display:flex;gap:6px;align-items:center;font-size:13px;margin:4px 0;cursor:pointer';
      const cb = el('input'); cb.type = 'checkbox'; cb.checked = on; cb.onchange = () => set(cb.checked, cb);
      l.append(cb, document.createTextNode(text)); return l;
    };
    if (supported) {
      const b = el('button', '', t(win ? 'pip.close' : 'pip.open')); b.type = 'button';
      b.style.cssText = 'font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:3px 12px;background:#FDD03B;cursor:pointer';
      b.onclick = async () => { if (win) close(); else await openWin(false); sync(); };
      setBtn = b;
      box.append(b, row(t('pip.settings.auto'), autoOn(), (v) => { setPref(AUTO_KEY, v ? '1' : '0'); wireAuto(); }));
      const p = el('p', '', t('pip.settings.autoHint')); p.style.cssText = 'font-size:12px;color:#666';
      box.append(p);
    } else {
      const p = el('p', '', t('pip.settings.unsupported')); p.style.cssText = 'font-size:12px;color:#666'; box.append(p);
    }
    if ('Notification' in window) {
      box.append(row(t('pip.settings.notify'), notifyOn(), async (v, cb) => {
        setPref(NOTIFY_KEY, v ? '1' : '0');
        if (v && Notification.permission !== 'granted') {
          const r = await Notification.requestPermission().catch(() => 'denied');
          if (r !== 'granted') { cb.checked = false; o.toast(t('pip.settings.notifyDenied')); }
        }
      }));
    }
  } });

  // ---- the window's contents
  let sheetP = null; // the pixel icons' sheet as a blob: URL, fetched once
  const sheet = () => (sheetP ??= fetch('/icons/ui-icons.png').then((r) => (r.ok ? r.blob() : null)).then((b) => (b ? URL.createObjectURL(b) : null)).catch(() => null));
  const avatars = new Map(); // id -> {key, canvas}: an otter drawn once per look
  function otter(p, id, size) {
    const look = o.lookOf(p), key = JSON.stringify(look);
    let a = avatars.get(id);
    if (!a || a.key !== key) {
      const c = a?.canvas ?? el('canvas'); c.width = c.height = size;
      const g = c.getContext('2d'); g.imageSmoothingEnabled = false; g.clearRect(0, 0, size, size);
      try { g.drawImage(o.buildSheet(o.otterImgs, look), 0, 0, 32, 32, 0, 0, size, size); } catch {}
      a = { key, canvas: c }; avatars.set(id, a);
    }
    return a.canvas;
  }
  function build(doc) {
    doc.title = 'Raft Deck';
    doc.documentElement.lang = lang; // (its own <html lang>: the font stack above)
    const offLang = onLang(() => { doc.documentElement.lang = lang; }); doc.defaultView?.addEventListener('pagehide', offLang, { once: true });
    // the pixel icons' CSS (icons.js puts it in the page's head), with the sheet's URL made absolute for this window
    // (as a blob: URL of the page's own copy: a PiP window was seen not loading it by URL)
    const px = document.getElementById('px-icon-css')?.textContent ?? '';
    const st = doc.createElement('style'), css = (u) => px.replaceAll('url(/icons/ui-icons.png)', `url(${u})`).replaceAll('url(/', `url(${location.origin}/`) + '\n' + CSS;
    st.textContent = css(location.origin + '/icons/ui-icons.png'); doc.head.append(st);
    sheet().then((u) => { if (u) st.textContent = css(u); });
    const b = doc.body;
    const ib = (cls = '') => { const x = el('button', 'ib' + (cls ? ' ' + cls : '')); x.type = 'button'; return x; };
    /** an icon button's look and words: its icon, its class (on / off / live ...), its tooltip (also what's read out) */
    const look = (x, name, cls, words) => {
      if (x.dataset.icon !== name) { x.dataset.icon = name; x.replaceChildren(icon(name)); }
      x.className = 'ib' + (cls ? ' ' + cls : ''); x.title = words; x.setAttribute('aria-label', words);
    };
    // ---- the top bar: my status (a dot), what the map shows (附近的人 / the meeting) and its count, 回到 Deck
    const top = el('div', 'bar');
    // my status: the UI's one dropdown (public/dropdown.js) as a small icon button, its list opening in this window
    const states = () => Object.entries(o.STATES).map(([id, st]) => ({ value: id, text: st.name, icon: st.icon, hint: id === 'dnd' ? o.dndHint : undefined }));
    const dd = dropdown({ doc, label: tip('status'), className: 'ib', value: 'available', options: states(),
      // the button shows my status as it is (an automatic 离开 included); the list marks the one set by hand
      render: (c, x) => {
        const s = o.statusOf(o.me()), d = o.STATES[s?.s ?? 'available'], words = tip(s?.auto ? 'statusAuto' : 'statusNow', { name: d.name });
        x.replaceChildren(icon(d.icon)); x.title = words; x.setAttribute('aria-label', words);
      },
      always: true, onChange: act((v) => o.setStatus(v)) }); // always: a pick the office refused can be made again
    const hdT = el('span', 't'), hdN = el('span', 'n'), hdC = el('span', 'c'); hdT.append(hdN, hdC);
    const back = ib('go'); // (a return arrow: the door is 离开 a meeting)
    // the page to the front, and the window goes (what it opened by itself or not: nothing opens it again until the tab
    // goes to the background next time)
    back.onclick = () => { try { window.focus(); } catch {} close(); };
    top.append(dd.el, hdT, back);
    // ---- the bottom bar: mic, sound (its volume in a small pop-up), and in a meeting the camera and 离开 / 挂断;
    // 停止共享 while I share
    const bot = el('div', 'bar bot');
    const bMic = ib(), bVol = ib(), bCam = ib(), bShare = ib(), leave = ib('red');
    bMic.onclick = act(() => o.mic.toggle()); bShare.onclick = act(() => o.share.stop());
    // the camera's permission prompt shows on the page, not here: if it will ask, bring the page to the front first
    bCam.onclick = async () => {
      try { if (document.hidden && (await navigator.permissions.query({ name: 'camera' })).state === 'prompt') { window.focus(); o.toast(t('pip.camPrompt')); } } catch {}
      act(() => o.meeting.toggleCam())();
    };
    leave.onclick = act(() => o.meeting.leave());
    // the volume: a click on the speaker opens a small bar over the bottom one (关闭声音, the slider, the number)
    const pop = el('div', 'pop'), bOut = ib(), range = el('input'), volN = el('span', 'n');
    pop.hidden = true; pop.setAttribute('role', 'group');
    range.type = 'range'; range.min = '0'; range.max = '100';
    range.oninput = act(() => o.speaker.setVolume(range.value / 100)); // (above 0 it turns the sound back on: app.js speaker)
    bOut.onclick = act(() => o.speaker.setOff(!o.speaker.off()));
    pop.append(bOut, range, volN);
    const popOpen = (on) => { pop.hidden = !on; bVol.setAttribute('aria-expanded', String(on)); if (on) range.focus(); };
    bVol.setAttribute('aria-haspopup', 'true'); bVol.setAttribute('aria-expanded', 'false');
    bVol.onclick = act(() => popOpen(pop.hidden));
    doc.addEventListener('pointerdown', (e) => { if (!pop.hidden && !pop.contains(e.target) && !bVol.contains(e.target)) popOpen(false); }, { capture: true });
    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !pop.hidden) { popOpen(false); bVol.focus(); } });
    bot.append(bMic, bVol, bCam, bShare, leave, pop);
    // ---- the middle: a 双人互动 request and invites to me, then the map (附近的人) or the meeting
    const main = el('div', 'main');
    const room = el('div', 'room');
    const vid = el('div', 'vid'), video = el('video'), who = el('span', 'who');
    video.autoplay = true; video.playsInline = true; video.muted = true; vid.append(video, who);
    const ppl = el('div', 'ppl');
    // 附近的人: the minimap, with a person's card over it (a click on them)
    const mm = el('div', 'mm'), cv = el('canvas'), pc = el('div', 'pc'), none = el('div', 'empty');
    pc.hidden = true; mm.append(cv, none, pc);
    cv.addEventListener('pointermove', (e) => { const h = spotAt(e); hover = h?.id ?? null; cv.title = h ? h.name : ''; cv.style.cursor = h && !h.me ? 'pointer' : ''; });
    cv.addEventListener('pointerleave', () => { hover = null; });
    cv.addEventListener('click', (e) => { const h = spotAt(e); sel = h && !h.me ? h.id : null; drawCard(); });
    doc.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sel) { sel = null; drawCard(); } });
    room.append(vid, ppl, mm);
    const invs = el('div'), duoBox = el('div'); invs.style.cssText = duoBox.style.cssText = 'display:flex;flex-direction:column;gap:6px;flex:none';
    main.append(duoBox, invs, room);
    b.append(top, main, bot);
    // I'm using it: that's not 离开 (public/status.js)
    for (const ev of ['pointerdown', 'keydown']) doc.addEventListener(ev, () => o.poke(), { capture: true, passive: true });
    // the words set once here (again on a language change: onLang above)
    const relabel = () => {
      look(back, 'undo', 'go', tip('back')); // (a return arrow: the door is 离开 a meeting)
      pop.setAttribute('aria-label', tip('vol')); range.setAttribute('aria-label', tip('vol'));
      none.textContent = t('pip.map.empty'); cv.setAttribute('aria-label', t('pip.map.label'));
      dd.setOptions(states(), dd.value());
    };
    relabel();
    ui = { relabel, look, dd, bMic, bVol, bCam, bShare, bOut, pop, range, volN, room, hdT, hdN, hdC, leave, vid, video, who, ppl, mm, cv, pc, none, invs, duoBox, spots: [], sig: { ppl: '', inv: '', pc: '', duo: '' } };
  }

  // ---- the minimap (附近的人): the area round me, from the map the game uses (o.MAP: its tiles as the editor left them,
  // its rooms), drawn on the window's own frames (frame above): the page behind may be hidden and draw nothing
  let sel = null, hover = null, mapAt = 0; // the person whose card is open; the one under the pointer; the minimap's last frame
  const fitted = new Map(); // name -> it cut to the label's width (measureText per character is slow)
  let base = null; // {solid, water, canvas}: the map at 1 px a tile (deck, walls and furniture, sea), again when it changes
  function baseMap() {
    const M = o.MAP; if (!M) return null;
    if (base?.solid === M.solid && base.water === M.water) return base.canvas;
    const c = el('canvas'); c.width = M.width; c.height = M.height;
    const g = c.getContext('2d'), img = g.createImageData(M.width, M.height);
    for (let y = 0; y < M.height; y++) for (let x = 0; x < M.width; x++) {
      const k = (y * M.width + x) * 4, rgb = M.water[y][x] === '1' ? MM_SEA : M.solid[y][x] === '1' ? MM_WALL : MM_DECK;
      img.data[k] = rgb[0]; img.data[k + 1] = rgb[1]; img.data[k + 2] = rgb[2]; img.data[k + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    base = { solid: M.solid, water: M.water, canvas: c };
    return c;
  }
  /** The person under a pointer event on the minimap (the nearest within reach), from the last frame's spots. */
  function spotAt(e) {
    let best = null, bd = MM_AV * 0.6;
    for (const s of ui?.spots ?? []) { if (s.me) continue; const d = Math.hypot(e.offsetX - s.x, e.offsetY - s.y); if (d < bd) { bd = d; best = s; } } // (me: never, so someone standing on me can be picked)
    return best;
  }
  function drawMap() {
    if (!ui || !win || ui.mm.style.display === 'none') return;
    const me = o.me(), M = o.MAP; if (!me || !M) return;
    const cv = ui.cv, cw = cv.clientWidth, ch = cv.clientHeight; if (!cw || !ch) return;
    const dpr = win.devicePixelRatio || 1;
    if (cv.width !== Math.round(cw * dpr) || cv.height !== Math.round(ch * dpr)) { cv.width = Math.round(cw * dpr); cv.height = Math.round(ch * dpr); }
    const g = cv.getContext('2d'), S = MM_TILE;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    // me in the middle (the middle of my body), whole pixels so the tiles stay crisp
    const ox = Math.round(cw / 2 - (me.x + 0.5) * S), oy = Math.round(ch / 2 - (me.y + 0.6) * S);
    g.fillStyle = `rgb(${MM_SEA})`; g.fillRect(0, 0, cw, ch);
    g.imageSmoothingEnabled = false;
    const b = baseMap(); if (b) g.drawImage(b, ox + (M.x0 ?? 0) * S, oy + (M.y0 ?? 0) * S, M.width * S, M.height * S); // (a grown map starts at x0, y0)
    // rooms, 会议室 and huts: an outline and their name
    g.font = `10px ${uiFont()}`; g.textBaseline = 'top';
    for (const z of M.zones) {
      const x = ox + z.x * S, y = oy + z.y * S, w = z.w * S, h = z.h * S;
      if (x > cw || y > ch || x + w < 0 || y + h < 0) continue;
      g.fillStyle = z.meeting ? 'rgba(253,208,59,.28)' : 'rgba(255,248,231,.22)'; g.fillRect(x, y, w, h);
      g.strokeStyle = z.meeting ? '#b45309' : 'rgba(27,20,16,.7)'; g.lineWidth = 1; g.setLineDash([3, 2]); g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1); g.setLineDash([]);
      if (w > 28 && z.name) { g.fillStyle = 'rgba(27,20,16,.75)'; g.fillText(fit(g, z.name, w - 4), x + 2, y + 2); }
    }
    // how far I'm heard on the open deck
    if (!o.zoneAt(me.x, me.y)) { g.strokeStyle = 'rgba(34,197,94,.55)'; g.setLineDash([4, 3]); g.beginPath(); g.arc(cw / 2, ch / 2, MM_HEAR * S, 0, Math.PI * 2); g.stroke(); g.setLineDash([]); }
    // the people: everyone in view, the ones I hear clear, the rest faded; me last, on top
    const hear = o.hearing(), caster = o.caster(), spots = [];
    const at = (p) => [ox + (p.x + 0.5) * S, oy + (p.y + 0.6) * S];
    const people = [...o.peers()].map(([id, p]) => ({ id, p, xy: at(p) }))
      .filter(({ xy: [x, y] }) => x > -MM_AV && y > -MM_AV && x < cw + MM_AV && y < ch + MM_AV)
      .sort((a, b) => a.p.y - b.p.y);
    people.push({ id: me.id, p: me, xy: [cw / 2, ch / 2], me: true });
    for (const { id, p, xy: [x, y], me: mine } of people) {
      const r = MM_AV / 2, heard = mine || hear.has(id) || id === caster;
      g.globalAlpha = heard ? 1 : 0.55;
      g.fillStyle = '#e9d8b4'; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
      g.save(); g.beginPath(); g.arc(x, y, r - 1, 0, Math.PI * 2); g.clip();
      g.imageSmoothingEnabled = true; g.drawImage(otter(p, 'mm:' + (mine ? 'me' : id), 32), x - r, y - r, MM_AV, MM_AV); g.restore();
      const talking = talks(p);
      g.lineWidth = talking || id === sel ? 3 : mine ? 2.5 : 1.5;
      g.strokeStyle = talking ? '#22c55e' : id === sel ? '#FDD03B' : '#1b1410';
      g.beginPath(); g.arc(x, y, r + (talking || id === sel ? 1 : 0), 0, Math.PI * 2); g.stroke();
      g.globalAlpha = 1;
      spots.push({ id, x, y, me: !!mine, name: mine ? t('pip.you') : o.agentName(p) });
    }
    // names under them, where they don't run into one already written (the hovered one and the open card's first)
    const boxes = [], order = [...spots].sort((a, b) => (b.id === hover) - (a.id === hover) || (b.id === sel) - (a.id === sel) || a.me - b.me); // mine last: I know where I am
    g.textBaseline = 'middle'; g.font = `10px ${uiFont()}`;
    for (const s of order) {
      const t = fitted.get(s.name) ?? (fitted.size > 500 && fitted.clear(), fitted.set(s.name, fit(g, s.name, 64)).get(s.name)), w = g.measureText(t).width + 6, bx = s.x - w / 2, by = s.y + MM_AV / 2 + 2, bh = 13;
      if (boxes.some((q) => bx < q.x + q.w && bx + w > q.x && by < q.y + q.h && by + bh > q.y)) continue;
      boxes.push({ x: bx, y: by, w, h: bh });
      g.fillStyle = s.me ? '#FDD03B' : 'rgba(255,248,231,.92)'; g.fillRect(bx, by, w, bh);
      g.strokeStyle = '#1b1410'; g.lineWidth = 1; g.strokeRect(bx + 0.5, by + 0.5, w - 1, bh - 1);
      g.fillStyle = '#1b1410'; g.fillText(t, bx + 3, by + bh / 2 + 0.5);
    }
    ui.spots = spots;
  }
  /** text cut to fit w px, with … */
  function fit(g, text, w) {
    if (g.measureText(text).width <= w) return text;
    let t = [...text]; while (t.length > 1 && g.measureText(t.join('') + '…').width > w) t.pop();
    return t.join('') + '…';
  }
  /** The card of the person clicked on the minimap: who, where, their status, and what their card on the page offers. */
  function drawCard() {
    if (!ui) return;
    const p = sel && o.peers().get(sel);
    if (!p) { sel = null; ui.pc.hidden = true; ui.sig.pc = ''; return; }
    const acts = o.card?.actions(p) ?? [], url = o.card?.profile?.(p) ?? null, s = o.statusOf(p), z = o.zoneAt(p.x, p.y);
    const where = z ? z.name : t(p.boat ? 'pip.card.sea' : 'pip.card.deck');
    const line = [s ? o.statusLine(s) : null, where, p.kind === 'agent' ? t('pip.card.agent') : null].filter(Boolean).join(' · ');
    const sig = JSON.stringify([p.id, o.agentName(p), line, acts.map((a) => [a.icon, a.label, !!a.disabled, a.title ?? '']), url, JSON.stringify(o.lookOf(p))]);
    ui.pc.hidden = false;
    if (sig === ui.sig.pc) return;
    ui.sig.pc = sig;
    const top = el('div', 'top'), who = el('div', 'who'), nm = el('span', 'nm'), wh = el('span', 'wh', line);
    put(nm, p.kind === 'agent' ? 'robot' : null, o.agentName(p)); nm.title = o.agentName(p); wh.title = line;
    who.append(nm, wh);
    const x = el('button', 'x'); x.type = 'button'; x.title = t('pip.card.close'); x.append(icon('close')); x.onclick = () => { sel = null; drawCard(); };
    top.append(otter(p, 'pc:' + p.id, 64), who, x);
    const row = el('div', 'acts');
    for (const a of acts) {
      const b = label(el('button', a.go && !a.disabled ? 'go' : ''), a.icon, a.label); b.type = 'button'; b.disabled = !!a.disabled || !a.go; b.title = a.title ?? '';
      // what it does now (the person may have moved since this card was drawn)
      b.onclick = act(() => { const q = o.peers().get(p.id); const f = q && (o.card?.actions(q) ?? []).find((y) => y.icon === a.icon && y.label === a.label); if (f && !f.disabled) f.go?.(); });
      row.append(b);
    }
    if (url) { const l = put(el('a'), 'chat', t('pip.card.dm')); l.href = url; l.target = '_blank'; l.rel = 'noopener'; l.title = t('pip.card.profile'); row.append(l); }
    ui.pc.replaceChildren(top, row);
  }

  const talks = (p) => (p.kind !== 'agent' || !!p.clip) && o.speaking(p); // agents talk only in clips (as meeting.js)
  function draw() {
    if (!ui || !win) return;
    const me = o.me();
    if (!me) { close(); return; } // left the office (another tab took over, a floor switch)
    // my status
    const s = o.statusOf(me), d = o.STATES[s?.s ?? 'available'];
    const dk = d.icon + (s?.auto ? 'a' : ''), hand = s && !s.auto ? s.s : 'available';
    // the list's check is the status set by hand as the office holds it: a pick it refused (too quick) goes back to that
    if (ui.dd.el.dataset.k !== dk + hand || ui.dd.value() !== hand) { ui.dd.el.dataset.k = dk + hand; ui.dd.set(hand); } // set() redraws the button (render)
    // mic (red while muted), camera, share
    const muted = o.mic.muted(), ready = o.mic.ready(), L = ui.look;
    L(ui.bMic, muted || !ready ? 'mic_off' : 'mic', !ready ? '' : muted ? 'off' : 'on', tip(!ready ? 'micNone' : muted ? 'micOff' : 'micOn')); ui.bMic.disabled = !ready;
    const mt = o.meeting.info();
    ui.bCam.style.display = mt ? '' : 'none'; // cameras are for 会议室
    if (mt) { L(ui.bCam, mt.cam.on ? 'cam' : 'cam_off', mt.cam.on ? 'on' : '', tip(mt.cam.busy ? 'camBusy' : mt.cam.on ? 'camOn' : 'camOff')); ui.bCam.disabled = mt.cam.busy; }
    ui.bShare.style.display = o.share.live() ? '' : 'none'; L(ui.bShare, 'screen', 'live', tip('shareStop'));
    // speaker: the button shows it off (red) or on, with the volume in its tooltip; the pop-up has the rest
    const off = o.speaker.off(), v = Math.round(o.speaker.volume() * 100);
    L(ui.bVol, off ? 'speaker_off' : 'speaker', off ? 'off' : '', tip(off ? 'volOff' : 'volLevel', { v }));
    L(ui.bOut, off ? 'speaker_off' : 'speaker', off ? 'on' : '', tip(off ? 'soundOn' : 'soundOff'));
    if (ui.range.ownerDocument.activeElement !== ui.range) ui.range.value = String(v);
    ui.volN.textContent = off ? t('pip.volOff') : v + '%';
    // the meeting, or who's near me
    let list;
    ui.room.classList.toggle('card', !!mt);
    if (mt) {
      put(ui.hdN, mt.call ? 'phone' : 'cam', mt.name); ui.hdT.title = mt.name; ui.hdC.textContent = t('pip.meeting.count', { n: mt.list.length });
      ui.leave.style.display = ''; L(ui.leave, mt.call ? 'hangup' : 'door', 'red', tip(mt.call ? 'hangup' : 'leave'));
      list = mt.list.map((x) => ({ id: x.id, p: x.p, mine: x.mine, talking: talks(x.p) }));
      // the active speaker's camera; else anyone else's; else mine
      const others = mt.list.filter((x) => !x.mine && x.cam);
      const pick = others.find((x) => x.id === mt.active) ?? others[0] ?? mt.list.find((x) => x.mine && x.cam) ?? null;
      ui.vid.style.display = pick ? '' : 'none'; ui.vid.classList.toggle('mine', !!pick?.mine);
      const stream = pick?.cam ?? null;
      if (ui.video.srcObject !== stream) { ui.video.srcObject = stream; if (stream) ui.video.play().catch(() => {}); }
      if (pick) ui.who.textContent = pick.mine ? t('pip.you') : o.agentName(pick.p);
    } else {
      put(ui.hdN, 'people', tip('near')); ui.hdT.title = ''; ui.leave.style.display = 'none'; ui.vid.style.display = 'none';
      if (ui.video.srcObject) ui.video.srcObject = null;
      const hear = o.hearing(), caster = o.caster();
      const near = [...o.peers()].filter(([id]) => hear.has(id) || id === caster).length;
      ui.hdC.textContent = near ? t('pip.near.count', { n: near }) : '';
      ui.none.style.display = near || sel ? 'none' : '';
      // avatars of people gone from the office
      if (avatars.size > 3 * o.peers().size + 8) for (const k of avatars.keys()) { const id = k.replace(/^(mm|pc):/, ''); if (id !== 'me' && !o.peers().has(id)) avatars.delete(k); }
      list = [];
    }
    // in a 会议室 its people; else the minimap
    ui.ppl.style.display = mt ? '' : 'none'; ui.mm.style.display = mt ? 'none' : '';
    if (mt && sel) sel = null;
    drawCard();
    const sig = list.map((x) => x.id + ':' + x.p.name).join('|');
    if (sig !== ui.sig.ppl) {
      ui.sig.ppl = sig;
      ui.ppl.replaceChildren(...list.map((x) => {
        const a = el('div', 'av'); a.dataset.id = x.id; a.title = x.mine ? t('pip.youTag', { name: o.agentName(x.p) }) : o.agentName(x.p);
        a.append(otter(x.p, x.mine ? 'me' : x.id, 64).cloneNode(), el('span', '', x.mine ? t('pip.you') : o.agentName(x.p)));
        return a;
      }));
    }
    for (const a of ui.ppl.children) {
      const x = list.find((y) => y.id === a.dataset.id); if (!x) continue;
      a.classList.toggle('talk', x.talking);
      const c = a.querySelector('canvas'), src = otter(x.p, x.mine ? 'me' : x.id, 64);
      if (c && c.dataset.k !== avatars.get(x.mine ? 'me' : x.id).key) { c.dataset.k = avatars.get(x.mine ? 'me' : x.id).key; const g = c.getContext('2d'); g.clearRect(0, 0, 64, 64); g.drawImage(src, 0, 0); }
    }
    // a 双人互动 request to me: answered here as on the map (it would run out unseen while Deck is behind other windows)
    const dq = o.duo?.pending?.() ?? null, dsig = dq ? dq.id + '|' + dq.text : '';
    if (dsig !== ui.sig.duo) {
      ui.sig.duo = dsig; ui.duoBox.replaceChildren();
      if (dq) {
        const box = el('div', 'inv'), tx = el('div', 't'), bar = el('div', 'dt'), bt = el('div', 'bt');
        tx.append(...(dq.agent ? [icon('robot')] : []), el('span', '', dq.text), icon(dq.icon)); bar.append(el('i'));
        const yes = label(el('button', 'go'), 'check', t('pip.duo.yes')), no = label(el('button'), 'close', t('pip.duo.no')); yes.type = no.type = 'button';
        yes.onclick = act(() => o.duo.answer(true)); no.onclick = act(() => o.duo.answer(false));
        bt.append(yes, no); box.append(tx, bar, bt); ui.duoBox.append(box);
      }
    }
    if (dq) { const i = ui.duoBox.querySelector('.dt i'); if (i) i.style.width = `${Math.round(dq.left * 100)}%`; }
    // invites to me
    const cards = o.members.list(), isig = cards.map((c) => c.id + (c.folded ? 'f' : '') + (c.inGame ? 'g' : '')).join('|');
    if (isig !== ui.sig.inv) {
      ui.sig.inv = isig;
      ui.invs.replaceChildren(...cards.map((c) => {
        const box = el('div', 'inv' + (c.quiet ? ' quiet' : '')), tx = el('div', 't'); tx.append(icon(c.icon ?? 'door'), el('span', '', c.text));
        // in a game, as on the page's card (members.js): 「结束后再去」 if it can wait, else 接受 waits for the game to end
        const bt = el('div', 'bt'), yes = label(el('button', 'go'), c.inGame && c.defer ? 'check' : c.yesIcon ?? 'check', c.inGame && c.defer ? t('pip.inv.afterGame') : c.yes ?? t('pip.inv.accept')); yes.type = 'button';
        yes.disabled = c.inGame && !c.defer; yes.title = yes.disabled ? t('pip.inv.busyTip') : c.inGame ? t('pip.inv.afterGameTip') : c.yesTitle ?? t('pip.inv.walkTip');
        yes.onclick = act(() => o.members.answer(c.id, 'accept')); bt.append(yes);
        if (c.later && !c.folded) { const l = label(el('button'), 'timer', t('pip.inv.later')); l.type = 'button'; l.onclick = act(() => o.members.answer(c.id, 'later')); bt.append(l); }
        box.append(tx, bt); return box;
      }));
    }
  }

  return { open: () => !!win };
}
